import { MarketCountryCode } from '@axo/shared/types';
import { CreateApplicationFormDataValues } from './schema';
import { useContext, useEffect, useMemo } from 'react';
import { Product } from '@axo/shared/data-access/types';
import { getCountryCallingCode } from 'libphonenumber-js';
import parsePhoneNumber from 'libphonenumber-js';
import { useSearchParams } from 'react-router-dom';
import { useGetPersonByS2ID } from '@axo/shared/data-access/hooks/services/puzzel/useGetPersonByS2ID';
import { ApplicationContext } from '../../store/context';

type DeepExhaustive<T> = {
  [K in keyof Required<T>]: T[K] extends object ? DeepExhaustive<T[K]> : T[K];
};

export const useCreateApplicationFormDefaultValues = (
  marketCountry: MarketCountryCode,
): DeepExhaustive<CreateApplicationFormDataValues> => {
  const [searchParams] = useSearchParams();
  const s2_id = searchParams.get('s2_id');

  const { data: personData } = useGetPersonByS2ID(s2_id ?? undefined);
  const { dispatch } = useContext(ApplicationContext);

  // Update market country if s2 data has a different country
  useEffect(() => {
    if (
      personData?.MarketCountry &&
      personData.MarketCountry !== marketCountry
    ) {
      dispatch({
        scope: { parentType: 'marketCountry' },
        type: 'Set market country',
        payload: personData.MarketCountry,
      });
    }
  }, [personData?.MarketCountry, marketCountry, dispatch]);

  return useMemo(() => {
    const phoneE164 = personData?.Infos?.Phone;
    let parsedPhone: { countryCode: number; nationalNumber: string } = {
      countryCode: Number(getCountryCallingCode(marketCountry)) || NaN,
      nationalNumber: '',
    };

    if (phoneE164) {
      try {
        const parsed = parsePhoneNumber(phoneE164);
        if (parsed) {
          parsedPhone = {
            countryCode: Number(parsed.countryCallingCode) || NaN,
            nationalNumber: parsed.nationalNumber,
          };
        }
      } catch {
        // If parsing fails, keep default values
      }
    }

    return {
      Application: {
        AppliedAmount: NaN,
        LoanDuration: NaN,
        MarketCountry: marketCountry,
        Product: Product.UnsecuredLoan,
        Meta: {
          Brand: '',
        },
      },
      Person: {
        SocialSecurityNumber: personData?.SSN || personData?.Infos?.SSN || '',
      },
      Customer: {
        Email: personData?.Infos?.Email || '',
        MobilePhone: {
          CountryCode: parsedPhone.countryCode,
          Number: parsedPhone.nationalNumber,
        },
      },
    };
  }, [marketCountry, personData]);
};
